/**
 * Communications API service
 *
 * Provides functions for managing case communications and templates.
 */

import { apiClient } from './api'
import type {
  Communication,
  CommunicationsList,
  CreateOutboundRequest,
  RecordInboundRequest,
  ListCommunicationsQuery,
  CommunicationTemplate,
  CreateTemplateRequest,
  UpdateTemplateRequest,
  RenderTemplateRequest,
  RenderedTemplate,
  ListTemplatesQuery,
} from '../types/case-container'

// =============================================================================
// HELPERS
// =============================================================================

/**
 * Build query string from params object
 */
function buildQueryString(params: object): string {
  const searchParams = new URLSearchParams()
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== null) {
      searchParams.append(key, String(value))
    }
  }
  const queryString = searchParams.toString()
  return queryString ? `?${queryString}` : ''
}

// =============================================================================
// COMMUNICATION API
// =============================================================================

/**
 * List communications for a case
 */
export async function listCommunications(
  caseId: string,
  params?: ListCommunicationsQuery
): Promise<CommunicationsList> {
  const query = params ? buildQueryString(params) : ''
  return apiClient.get<CommunicationsList>(`/cases/${caseId}/communications${query}`)
}

/**
 * Get a single communication
 */
export async function getCommunication(
  caseId: string,
  communicationId: string
): Promise<Communication> {
  return apiClient.get<Communication>(`/cases/${caseId}/communications/${communicationId}`)
}

/**
 * Create an outbound communication
 */
export async function createOutbound(
  caseId: string,
  communication: CreateOutboundRequest
): Promise<Communication> {
  return apiClient.post<Communication>(`/cases/${caseId}/communications/outbound`, communication)
}

/**
 * Record an inbound communication
 */
export async function recordInbound(
  caseId: string,
  communication: RecordInboundRequest
): Promise<Communication> {
  return apiClient.post<Communication>(`/cases/${caseId}/communications/inbound`, communication)
}

/**
 * Delete a communication
 */
export async function deleteCommunication(caseId: string, communicationId: string): Promise<void> {
  await apiClient.delete(`/cases/${caseId}/communications/${communicationId}`)
}

// =============================================================================
// TEMPLATE API
// =============================================================================

/**
 * List communication templates for the user's organisation
 */
export async function listTemplates(params?: ListTemplatesQuery): Promise<CommunicationTemplate[]> {
  const query = params ? buildQueryString(params) : ''
  return apiClient.get<CommunicationTemplate[]>(`/org/communication-templates${query}`)
}

/**
 * Get a single template
 */
export async function getTemplate(templateId: string): Promise<CommunicationTemplate> {
  return apiClient.get<CommunicationTemplate>(`/org/communication-templates/${templateId}`)
}

/**
 * Create a new template
 */
export async function createTemplate(
  template: CreateTemplateRequest
): Promise<CommunicationTemplate> {
  return apiClient.post<CommunicationTemplate>('/org/communication-templates', template)
}

/**
 * Update a template
 */
export async function updateTemplate(
  templateId: string,
  updates: UpdateTemplateRequest
): Promise<CommunicationTemplate> {
  return apiClient.patch<CommunicationTemplate>(
    `/org/communication-templates/${templateId}`,
    updates
  )
}

/**
 * Delete a template
 */
export async function deleteTemplate(templateId: string): Promise<void> {
  await apiClient.delete(`/org/communication-templates/${templateId}`)
}

/**
 * Render a template with placeholder values
 */
export async function renderTemplate(
  templateId: string,
  request: RenderTemplateRequest
): Promise<RenderedTemplate> {
  return apiClient.post<RenderedTemplate>(
    `/org/communication-templates/${templateId}/render`,
    request
  )
}
